/**
 * React hook for error capture.
 *
 * Usage:
 *   const { captureException, captureMessage } = useErrorCapture();
 *
 *   try { ... } catch (error) {
 *     captureException(error, { traceId: '...' });
 *   }
 *
 * See Part 22 §128.2 of the Tech Guidelines.
 */

import { useCallback } from 'react';
import { useObservabilityClients } from '../../context/context-provider';
import type { ErrorLevel } from '../error-client.types';

export function useErrorCapture() {
  const { errors } = useObservabilityClients();

  const captureException = useCallback(
    (error: Error | unknown, context?: Record<string, unknown>) => {
      errors.captureException(error, { extra: context });
    },
    [errors]
  );

  const captureMessage = useCallback(
    (message: string, level: ErrorLevel = 'info', context?: Record<string, unknown>) => {
      errors.captureMessage(message, { level, extra: context });
    },
    [errors]
  );

  return { captureException, captureMessage };
}
